// App.jsx
// Root component – sets up React Router and wraps the app in Context providers.
// Routes are split into three groups: public, user-protected, and admin-only.

import React from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Context providers
import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";

// Layout + guards
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import ScrollToTop from "./components/ScrollToTop";

// Public / user pages
import Home from "./pages/Home";
import Products from "./pages/Products";
import Wishlist from "./pages/Wishlist";
import ProductDetail from "./pages/ProductDetail";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import OrderSuccess from "./pages/OrderSuccess";
import Orders from "./pages/Orders";
import Profile from "./pages/Profile";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import RefundPolicy from "./pages/RefundPolicy";

// Admin pages
import AdminLayout from "./pages/admin/AdminLayout";
import Dashboard from "./pages/admin/Dashboard";
import ManageProducts from "./pages/admin/ManageProducts";
import ManageOrders from "./pages/admin/ManageOrders";
import ManageUsers from "./pages/admin/ManageUsers";
import ManageSubscribers from "./pages/admin/ManageSubscribers";
import ManageSettings from "./pages/admin/ManageSettings";
import ManageOffers from "./pages/admin/ManageOffers";

export default function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <Router>
          <ScrollToTop />
          <ToastContainer position="top-right" autoClose={3000} />
          <Routes>
            {/* ── Admin routes (no Navbar/Footer – uses own AdminclearLayout sidebar) ── */}
            <Route
              path="/admin/*"
              element={
                <AdminRoute>
                  <AdminLayout />
                </AdminRoute>
              }
            >
              {/* Nested inside AdminLayout's <Outlet /> */}
              <Route index element={<Dashboard />} />
              <Route path="products" element={<ManageProducts />} />
              <Route path="orders" element={<ManageOrders />} />
              <Route path="users" element={<ManageUsers />} />
              <Route path="subscribers" element={<ManageSubscribers />} />
              <Route path="offers" element={<ManageOffers />} />
              <Route path="settings" element={<ManageSettings />} />
            </Route>

            {/* ── Store routes (with global Navbar + Footer) ────────────────────── */}
            <Route
              path="/*"
              element={
                <div className="min-h-screen bg-gray-50 flex flex-col">
                  <Navbar />
                  <main className="flex-1 flex flex-col">
                    <Routes>
                      {/* Public */}
                      <Route path="/" element={<Home />} />
                      <Route path="/products" element={<Products />} />
                      <Route path="/product/:id" element={<ProductDetail />} />
                      <Route path="/login" element={<Login />} />
                      <Route path="/signup" element={<Signup />} />
                      <Route path="/privacy" element={<PrivacyPolicy />} />
                      <Route path="/terms" element={<TermsOfService />} />
                      <Route path="/refund" element={<RefundPolicy />} />

                      {/* Requires login */}
                      <Route path="/cart" element={<ProtectedRoute><Cart /></ProtectedRoute>} />
                      <Route path="/wishlist" element={<ProtectedRoute><Wishlist /></ProtectedRoute>} />
                      <Route path="/checkout"
                        element={<ProtectedRoute><Checkout /></ProtectedRoute>}
                      />
                      <Route path="/order-success/:orderId"
                        element={<ProtectedRoute><OrderSuccess /></ProtectedRoute>}
                      />
                      <Route path="/orders"
                        element={<ProtectedRoute><Orders /></ProtectedRoute>}
                      />
                      <Route path="/profile"
                        element={<ProtectedRoute><Profile /></ProtectedRoute>}
                      />

                      {/* 404 */}
                      <Route path="*" element={
                        <div className="flex flex-col items-center justify-center py-20 text-center px-4">
                          <p className="text-7xl mb-6">😕</p>
                          <h2 className="text-2xl font-bold text-gray-900 mb-2">Page Not Found</h2>
                          <p className="text-gray-500 mb-6">The page you're looking for doesn't exist.</p>
                          <a href="/" className="bg-red-600 text-white px-6 py-2.5 rounded-xl font-semibold hover:bg-red-700 transition-colors">
                            Go Home
                          </a>
                        </div>
                      } />
                    </Routes>
                  </main>
                  <Footer />
                </div>
              }
            />
          </Routes>
        </Router>
      </CartProvider>
    </AuthProvider>
  );
}
